import $ from 'jquery';
import * as bootstrap from 'bootstrap';

import { renderRedemptionModal } from '/js/redeem.js';
import { logoutUser, userData, showSettings } from '/js/accounts.js';
import { v1BaseUrl } from './config.js';
import { mkToast } from '@shared/js/toasts.js';
import { renderLibrary } from '/js/library.js';
import { navigate, openModalRoute } from '/js/router.js';

// XSS protection
function escapeHtml(text) {
  return $("<div>").text(text).html();
}

function getDeviceType(ua) {
  ua = (ua || '').toLowerCase();
  if (ua.includes('cfnetwork')) {
    return 'Apple Device';
  }
  if (ua.includes('ipad') || ua.includes('tablet')) {
    return 'Tablet';
  }
  if (ua.includes('iphone') || ua.includes('android') || ua.includes('mobile')) {
    return 'Mobile';
  }
  return 'Desktop';
}

function userOptionsHtml() {
  // Define and Sanitize the variables
  const userGivenName = escapeHtml(userData.givenName);
  const userFamilyName = escapeHtml(userData.familyName);
  
  // Create elements
  const btnGroup = $('<div class="btn-group"></div>');
  const btnDropdown = $(`<button type="button" style="color: white !important; padding-left: 0.5rem; padding-right: 0.5rem;" class="btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"><i class="bi bi-person-fill"></i> ${userGivenName}</button>`);
  const dropdownMenu = $('<ul class="dropdown-menu dropdown-menu-end"></ul>');
  const settingsItem = $('<li><a class="dropdown-item">Account</a></li>');
  const securityItem = $('<li><a class="dropdown-item">Security</a></li>');
  const helpItem = $('<li><a class="dropdown-item">Help</a></li>');
  const refreshItem = $('<li><a class="dropdown-item">Refresh</a></li>');
  const divider1 = $('<li><hr class="dropdown-divider"></li>');
  let adminItem = $(`<li><a href="https://${import.meta.env.CMS_HOSTNAME}" class="dropdown-item">Administration</a></li>`);
  const divider2 = $('<li><hr class="dropdown-divider"></li>');
  const signOutItem = $('<li><a class="dropdown-item">Sign Out</a></li>');
  
  // Merge Elements — the Administration link appears for anyone who can
  // operate a CMS (platform admin or member of at least one organization).
  if (userData.cmsAccess || userData.admin) {
    dropdownMenu.append(settingsItem, securityItem, helpItem, refreshItem, divider1, adminItem, divider2, signOutItem);
  } else {
    dropdownMenu.append(settingsItem, securityItem, helpItem, refreshItem, divider1, signOutItem);
  }
  btnGroup.append(btnDropdown, dropdownMenu);
  
  // Add listeners
  refreshItem.on('click', function (event) {
	event.preventDefault();
	showLoader();
	window.location.reload();
  });
  
  settingsItem.on('click', function (event) {
    openModalRoute('settings');
  });

  securityItem.on('click', function (event) {
    openModalRoute('security');
  });
  
  helpItem.on('click', function (event) {
    window.open("https://help.stagevault.com/hc/2649884901", '_blank');
  });
  
  signOutItem.on('click', function (event) {
	event.preventDefault();
	showLoader();
	logoutUser();
  });
  
  return btnGroup;
}

export function navBarHtml(pageTitle) {

  // Create elements
  const navbar = $('<nav class="navbar navbar-dark sticky-top bg-danger-subtle"></nav>');
  const container = $('<div class="container"></div>');
  const hstack = $('<div class="hstack w-100 justify-content-between"></div>');
  const brand = $('<span class="navbar-brand mb-0 h1"></span>').text(pageTitle);
  const actions = $('<div class="hstack gap-2"></div>');
  const redeemButton = $('<a class="btn text-warning"><i class="bi bi-plus-lg"></i> Redeem</a>');
  const userOptions = userOptionsHtml();

  // Assemble the elements
  actions.append(redeemButton, userOptions);
  hstack.append(brand, actions);
  container.append(hstack);
  navbar.append(container);
  
  // Find dropdown element and initialize
  let dropdownElement = actions.find('.dropdown-toggle')[0];
  let dropdown = new bootstrap.Dropdown(dropdownElement);

  // Add the click event listener for the redeem button
  redeemButton.on('click', function (event) {
	event.preventDefault();
	openModalRoute('redeem');
  });

  return navbar;
}

// Navbar for the show details interstitial — leftmost back arrow returns to
// the vault, Redeem and the user dropdown stay accessible on the right, and
// the centered title is intentionally empty since the body already shows the
// show name as a large heading.
export function detailsNavBarHtml() {
  const navbar = $('<nav class="navbar navbar-dark sticky-top bg-danger-subtle"></nav>');
  const container = $('<div class="container"></div>');
  const hstack = $('<div class="hstack w-100 justify-content-between"></div>');
  const backButton = $('<a class="btn text-light px-2"><i class="bi bi-chevron-left me-1 fw-bold"></i>My Vault</a>');
  const actions = $('<div class="hstack gap-2"></div>');
  const redeemButton = $('<a class="btn text-warning"><i class="bi bi-plus-lg"></i> Redeem</a>');
  const userOptions = userOptionsHtml();

  actions.append(redeemButton, userOptions);
  hstack.append(backButton, $('<div class="flex-grow-1"></div>'), actions);
  container.append(hstack);
  navbar.append(container);

  // Same Dropdown bootstrapping pattern as navBarHtml above.
  const dropdownElement = actions.find('.dropdown-toggle')[0];
  if (dropdownElement) {
    new bootstrap.Dropdown(dropdownElement);
  }

  backButton.on('click', (event) => {
    event.preventDefault();
    navigate('/');
  });

  redeemButton.on('click', (event) => {
    event.preventDefault();
    openModalRoute('redeem');
  });

  return navbar;
}

export function watchNavBarHtml(showTitle, backTarget = '/') {
  // Build the HTML elements
  const nav = $('<nav class="navbar navbar-dark sticky-top bg-danger-subtle" style="height: 54px !important;"></nav>');
  const container = $('<div class="container"></div>');
  const hStack = $('<div class="d-flex w-100 justify-content-between align-items-center"></div>');
  const backButton = $('<a class="btn text-light"><i class="bi bi-chevron-left me-1 fw-bold"></i>Back</a>');
  const title = $('<span class="navbar-brand mb-0 h1 text-truncate" style="flex-grow: 1; text-align: center; max-width: calc(100% - 250px);"></span>').text(showTitle);
  const userOptions = userOptionsHtml();

  // Back goes wherever the caller asked — defaults to the vault.
  backButton.on('click', function() {
        navigate(backTarget);
  });

  // Assemble the elements
  hStack.append(backButton, title, userOptions);
  container.append(hStack);
  nav.append(container);

  return nav;
}

export var loader;

export function showLoader() {
	// Guard against stacking — hideLoader() removes by id, which only
	// matches the first element, so duplicates would linger forever.
	if ($('#loading-screen').length) {
		return;
	}
	loader = $('<div id="loading-screen" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999;" class="bg-danger-subtle"><div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"><div class="spinner-border text-white" role="status"><span class="visually-hidden">Loading...</span></div></div></div>');
	$('#app').append(loader);
}

export function hideLoader() {
  $('#loading-screen').remove();
}

export function showSecurity() {
  // Remove old modal if present
  if ($('#securityModal').length === 1) {
    $('#securityModal').remove();
  }

  const modal = $(`
        <div class="modal fade" id="securityModal" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" id="securityModalLabel">Security</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <h6 class="mb-3 fw-bold">Change Password</h6>
                <form id="changePasswordForm">
                  <div class="mb-3">
                    <label for="currentPassword" class="form-label">Current Password</label>
                    <input type="password" class="form-control" id="currentPassword" autocomplete="current-password" required>
                  </div>
                  <div class="mb-3">
                    <label for="newPassword" class="form-label">New Password</label>
                    <input type="password" class="form-control" id="newPassword" autocomplete="new-password" required>
                  </div>
                  <div class="mb-3">
                    <label for="confirmNewPassword" class="form-label">Confirm New Password</label>
                    <input type="password" class="form-control" id="confirmNewPassword" autocomplete="new-password" required>
                  </div>
                  <button type="submit" class="btn btn-primary">Update Password</button>
                </form>
                <hr/>
                <h6>Active Sessions</h6>
                <div id="sessionsComponent"><p>Loading...</p></div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>`);

  $('body').append(modal);

  const modalElement = $('#securityModal');
  const securityModalInstance = new bootstrap.Modal(modalElement);
  securityModalInstance.show();

  const sessionsContainer = $('#sessionsComponent');
  $.ajax({
    url: v1BaseUrl + 'account/sessions',
    type: 'GET',
    svErrorMode: 'default',
    xhrFields: {
      withCredentials: true
    },
    success: function(response) {
      const sessions = Array.isArray(response) ? response : (response.data || response.sessions);
      if (Array.isArray(sessions)) {
        const list = $('<ul class="list-group"></ul>');
        sessions.forEach(s => {
          const isCurrent = !!s.current;
          // API values go in via .text()/.attr() so they can never inject
          // markup or break out of attributes.
          const item = $(`<li class="list-group-item d-flex justify-content-between align-items-start">
              <div>
                <div><strong>IP:</strong> <span class="session-ip"></span></div>
                <div><strong>Time:</strong> <span class="session-time"></span>${s.device_login ? ' <span class="badge bg-secondary ms-1">Device Login</span>' : ''}</div>
                <div><strong>Device:</strong> <span class="session-device"></span>${isCurrent ? ' <span class="badge bg-primary ms-1">This Device</span>' : ''}</div>
              </div>
              <button class="btn btn-sm btn-danger revoke-session">Revoke</button>
            </li>`);
          item.find('.session-ip').text(s.ip || '');
          item.find('.session-time').text(new Date(s.created).toLocaleString());
          item.find('.session-device').text(getDeviceType(s.user_agent)).attr('title', s.user_agent || '');
          item.find('.revoke-session').attr('data-guid', s.guid);
          item.find('.revoke-session').on('click', function() {
            const guid = $(this).data('guid');
            $.ajax({
              url: v1BaseUrl + 'account/sessionRevoke',
              type: 'POST',
              svErrorMode: 'default',
              xhrFields: { withCredentials: true },
              data: { guid: guid },
              success: function(res) {
                if (res.success) {
                  item.remove();
                } else {
                  mkToast('security', 'danger', 'Error', res.error || 'Failed to revoke session', 5000);
                }
              },
              error: function() {
                mkToast('security', 'danger', 'Error', 'Failed to revoke session', 5000);
              }
            });
          });
          list.append(item);
        });
        sessionsContainer.empty().append(list);
      } else {
        sessionsContainer.text('Failed to load sessions.');
      }
    },
    error: function() {
      sessionsContainer.text('Failed to load sessions.');
    }
  });

  $('#changePasswordForm').on('submit', function(e) {
    e.preventDefault();
    const currentPassword = $('#currentPassword').val();
    const newPassword = $('#newPassword').val();
    const confirmNewPassword = $('#confirmNewPassword').val();
    if (newPassword !== confirmNewPassword) {
      mkToast('security', 'danger', 'Error', 'New passwords do not match', 5000);
      return;
    }
    $.ajax({
      url: v1BaseUrl + 'account/password',
      type: 'POST',
      svErrorMode: 'none',
      xhrFields: { withCredentials: true },
      data: { currentPassword: currentPassword, newPassword: newPassword },
      success: function(res) {
        if (res.success) {
          mkToast('security', 'success', 'Success', 'Password updated', 5000);
          $('#currentPassword').val('');
          $('#newPassword').val('');
          $('#confirmNewPassword').val('');
        } else {
          mkToast('security', 'danger', 'Error', res.error || 'Failed to change password', 5000);
        }
      },
      error: function(xhr) {
        const msg = xhr.responseJSON?.error || 'Failed to change password';
        mkToast('security', 'danger', 'Error', msg, 5000);
        if (xhr.status === 400) {
          $('#newPassword').val('');
          $('#confirmNewPassword').val('');
        }
      }
    });
  });

  // Native listener, not jQuery: Bootstrap triggers the jQuery event before
  // dispatching the native one, so a jQuery-bound removal detaches the
  // element and the router's document-level hash-cleanup listener never
  // fires, leaving #security stuck in the URL.
  modalElement[0].addEventListener('hidden.bs.modal', function () {
    this.remove();
  }, { once: true });
}

